import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  ClipboardList,
  Zap,
  Activity,
  Clock,
  Briefcase,
  PlusCircle,
  List,
  AlertCircle,
  Info,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Landmark,
  FileText,
  Upload
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { useToast } from './shared/Toast';
import { SkeletonKPICard, SkeletonActivityItem, Skeleton } from './shared/Skeleton';
import { TableEmptyState, ErrorState } from './shared/EmptyState';
import { AxiomButton, AxiomMetricCard, AxiomMetricGrid, AxiomStatusBadge } from './shared';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Card: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: React.ReactNode }> = 
  ({ children, className, title, icon }) => (
    <motion.div 
      variants={fadeInUp}
      className={cn("glass-card p-6 flex flex-col h-full", className)}
    >
      {(title || icon) && (
        <div className="flex items-center gap-3 mb-6">
          {icon && <div className="text-blue-400">{icon}</div>}
          {title && <h3 className="text-lg font-semibold text-white tracking-tight">{title}</h3>}
        </div>
      )}
      {children}
    </motion.div>
  );

// ============== Interfaces ==============
interface DashboardSummary {
  totalAum: number;
  totalAumFormatted: string;
  todayPnL: number;
  todayPnLFormatted: string;
  pnlPositive: boolean;
  openOrders: number;
  pendingExecution: number;
  riskScore: number;
  riskStatus: string;
  portfolioCount: number;
  positionCount: number;
}

interface ActivityItem {
  id: number;
  type: string;
  title: string;
  time: string;
  user?: string;
}

interface TopPortfolio {
  id: number;
  name: string;
  aum: number;
  aumFormatted: string;
}

interface MarketIndicator {
  value: number;
  change: number;
  unit: string;
}

// ============== Main Component ==============
const DashboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [topPortfolios, setTopPortfolios] = useState<TopPortfolio[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [yieldCurve, setYieldCurve] = useState<any>(null);
  const [marketIndicators, setMarketIndicators] = useState<{
    fedFundsRate?: MarketIndicator;
    igSpread?: MarketIndicator;
    hySpread?: MarketIndicator;
  }>({});
  const [marketStatus, setMarketStatus] = useState<{
    session?: string;
    isOpen?: boolean;
    label?: string;
    nextChangeLabel?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async (showToast = false) => {
    try {
      setError(null);
      const [summaryRes, portfoliosRes, activityRes, yieldRes, indicatorsRes, marketStatusRes] = await Promise.all([
        axios.get('/api/dashboard/summary').catch(() => ({ data: null })),
        axios.get('/api/dashboard/top-portfolios?limit=5').catch(() => ({ data: [] })),
        axios.get('/api/dashboard/recent-activity?limit=5').catch(() => ({ data: [] })),
        axios.get('/api/finra-trace/yield-curve').catch(() => ({ data: null })),
        axios.get('/api/dashboard/market-indicators').catch(() => ({ data: {} })),
        axios.get('/api/dashboard/market-status').catch(() => ({ data: null }))
      ]);

      if (summaryRes.data?.success) setSummary(summaryRes.data);
      setTopPortfolios(Array.isArray(portfoliosRes.data) ? portfoliosRes.data : []);
      setRecentActivity(Array.isArray(activityRes.data) ? activityRes.data : []);
      setYieldCurve(yieldRes.data);
      setMarketIndicators(indicatorsRes.data || {});
      setMarketStatus(marketStatusRes.data || null);

      if (showToast) {
        toast.success('Dashboard refreshed', 'Latest data loaded successfully');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      toast.error('Failed to refresh', 'Could not load the latest data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(() => fetchDashboardData(false), 60000);
    return () => clearInterval(interval);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto space-y-8">
      {/* Header Section */}
      <motion.div
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        className="flex flex-col md:flex-row md:items-end justify-between gap-4"
      >
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight mb-2">
            {getGreeting()}, <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">{user?.fullName || 'Trader'}</span>
          </h1>
          <div className="flex items-center gap-3 text-slate-500 font-medium">
            <Clock className="w-4 h-4" />
            <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <AxiomButton
            onClick={() => fetchDashboardData(true)}
            disabled={loading}
            aria-label="Refresh dashboard"
            $variant="secondary"
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            Refresh
          </AxiomButton>
          {(() => {
            const session = marketStatus?.session;
            const isOpen = marketStatus?.isOpen;
            const label = marketStatus?.label ?? 'Markets';
            // OPEN → emerald (live pulse); PRE_MARKET/AFTER_HOURS → amber; CLOSED/unknown → slate
            const tone = isOpen ? 'emerald' : session === 'PRE_MARKET' || session === 'AFTER_HOURS' ? 'amber' : 'slate';
            return (
              <AxiomStatusBadge $tone={tone} title={marketStatus?.nextChangeLabel || undefined}>
                <Activity className="h-3 w-3" />
                {label}
              </AxiomStatusBadge>
            );
          })()}
        </div>
      </motion.div>

      {/* Market Indicators Ticker */}
      <motion.div 
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        className="flex gap-8 overflow-x-auto pb-2 scrollbar-hide no-scrollbar"
      >
        {[
          { label: '10Y Treasury', value: yieldCurve?.yields?.['10Y'], change: '--' },
          { label: '2Y Treasury', value: yieldCurve?.yields?.['2Y'], change: '--' },
          { label: 'Fed Funds', value: marketIndicators.fedFundsRate?.value, change: marketIndicators.fedFundsRate?.change },
          { label: 'IG Spread', value: marketIndicators.igSpread?.value, change: marketIndicators.igSpread?.change, unit: 'bps' },
          { label: 'HY Spread', value: marketIndicators.hySpread?.value, change: marketIndicators.hySpread?.change, unit: 'bps' },
          { label: 'Lev Loan', value: (marketIndicators as any).levLoanIndex?.value, change: (marketIndicators as any).levLoanIndex?.change, unit: '' },
          { label: 'PE NAV', value: (marketIndicators as any).peNavIndex?.value, change: (marketIndicators as any).peNavIndex?.change, unit: '' },
        ].filter(item => item.value !== undefined || ['10Y Treasury', '2Y Treasury', 'Fed Funds', 'IG Spread', 'HY Spread'].includes(item.label)).map((item, i) => (
          <div key={i} className="flex items-center gap-3 shrink-0 px-4 py-2 bg-slate-900/40 border border-white/5 rounded-xl">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{item.label}</span>
            <span className="text-sm font-bold text-white">{item.value ? `${item.value.toFixed(2)}${item.unit || '%'}` : '--'}</span>
            {item.change !== undefined && (
              <span className={cn("text-xs font-bold", Number(item.change) >= 0 ? "text-emerald-400" : "text-red-400")}>
                {Number(item.change) >= 0 ? '+' : ''}{item.change}
              </span>
            )}
          </div>
        ))}
      </motion.div>

      {/* KPI Grid */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        {loading ? (
          // Skeleton loaders while loading
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SkeletonKPICard />
            <SkeletonKPICard />
            <SkeletonKPICard />
            <SkeletonKPICard />
          </div>
        ) : error ? (
          // Error state
          <div>
            <ErrorState
              error={error}
              onRetry={() => fetchDashboardData(true)}
            />
          </div>
        ) : (
          // Actual KPI cards
          <AxiomMetricGrid>
            <AxiomMetricCard
              label="Total AUM"
              value={summary?.totalAumFormatted || '$0'}
              subtext={`${summary?.portfolioCount || 0} portfolios`}
              icon={<DollarSign className="w-5 h-5" />}
              tone="cyan"
            />
            <AxiomMetricCard
              label="Unrealized P&L"
              value={summary?.todayPnLFormatted || '$0'}
              subtext={summary?.pnlPositive ? 'Positive mark movement' : 'Negative mark movement'}
              icon={summary?.pnlPositive ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              tone={summary?.pnlPositive ? 'emerald' : 'red'}
            />
            <AxiomMetricCard
              label="Open Orders"
              value={String(summary?.openOrders || 0)}
              subtext={`${summary?.pendingExecution || 0} pending execution`}
              icon={<ClipboardList className="w-5 h-5" />}
              tone="violet"
            />
            <AxiomMetricCard
              label="Risk Score"
              value={`${summary?.riskScore || 0}/100`}
              subtext={summary?.riskStatus || 'Calculating...'}
              icon={<Zap className="w-5 h-5" />}
              tone={(summary?.riskScore || 0) >= 70 ? 'emerald' : 'amber'}
            />
          </AxiomMetricGrid>
        )}
      </motion.div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Card title="Command Surface" icon={<Zap className="w-5 h-5" />}>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <AxiomStatusBadge $tone="cyan">Portfolio Context</AxiomStatusBadge>
              <AxiomStatusBadge $tone="violet">Trade / Risk / Ops</AxiomStatusBadge>
              <AxiomStatusBadge $tone="emerald">IBOR-linked</AxiomStatusBadge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full">
              {[
                { label: 'New Trade', desc: 'Fixed income & credit', icon: <PlusCircle />, to: '/trade-entry/fixed-income' },
                { label: 'Alternatives', desc: 'PE, RE, private credit', icon: <Landmark />, to: '/portfolio-management?tab=alternatives' },
                { label: 'Risk Analytics', desc: 'VaR & stress tests', icon: <BarChart3 />, to: '/analytics' },
                { label: 'Trade Blotter', desc: 'Real-time order monitoring', icon: <List />, to: '/bond-transactions' },
                { label: 'Document Inbox', desc: 'Loan notices & credit agreements', icon: <Upload />, to: '/middle-office?tab=document-processing' },
                { label: 'Credit & Loans', desc: 'Covenants & lifecycle', icon: <FileText />, to: '/portfolio-management?tab=credit-loans' },
              ].map((action, i) => (
                <button 
                  key={i}
                  onClick={() => navigate(action.to)}
                  className="flex flex-col p-5 bg-slate-950/50 border border-white/5 rounded-2xl hover:border-blue-500/30 hover:bg-blue-500/5 transition-all group text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-blue-400 transition-colors mb-4">
                    {action.icon}
                  </div>
                  <div className="font-bold text-white mb-1">{action.label}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{action.desc}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card title="Top Portfolios" icon={<Briefcase className="w-5 h-5" />}>
            <div className="space-y-3 h-full">
              {loading ? (
                // Skeleton loaders
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-950/30 border border-white/5 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Skeleton className="w-2 h-2 rounded-full" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-4 w-20" />
                    </div>
                  ))}
                </>
              ) : topPortfolios.length === 0 ? (
                <TableEmptyState
                  message="No active portfolios found"
                  onAction={() => navigate('/portfolio-management')}
                  actionLabel="Create portfolio"
                />
              ) : (
                topPortfolios.map((p, i) => (
                  <div
                    key={i}
                    onClick={() => navigate('/portfolio-management')}
                    className="interactive-row flex items-center justify-between p-4 bg-slate-950/30 border border-white/5 rounded-xl group"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate('/portfolio-management')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="text-sm font-semibold text-slate-200">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono font-bold text-white">{p.aumFormatted}</span>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card title="Recent Activity" icon={<Activity className="w-5 h-5" />}>
          <div className="space-y-4">
            {loading ? (
              // Skeleton loaders
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <SkeletonActivityItem key={i} />
                ))}
              </>
            ) : recentActivity.length === 0 ? (
              <TableEmptyState message="No recent activity" />
            ) : (
              recentActivity.map((activity, i) => (
                <div key={i} className="interactive-row flex gap-4 p-3 rounded-xl group">
                  <div className={cn("shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border border-white/5",
                    activity.type === 'buy' ? 'bg-emerald-500/10 text-emerald-400' :
                    activity.type === 'sell' ? 'bg-red-500/10 text-red-400' :
                    activity.type === 'alert' ? 'bg-amber-500/10 text-amber-400' :
                    'bg-blue-500/10 text-blue-400'
                  )}>
                    {activity.type === 'buy' ? <ArrowUpRight className="w-5 h-5" /> :
                     activity.type === 'sell' ? <ArrowDownRight className="w-5 h-5" /> :
                     activity.type === 'alert' ? <AlertCircle className="w-5 h-5" /> :
                     <Info className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-200 truncate group-hover:text-white transition-colors">{activity.title}</div>
                    <div className="text-xs text-slate-500 font-medium">{activity.time}</div>
                  </div>
                </div>
              ))
            )}
          </div>
          <button
            onClick={() => navigate('/bond-transactions')}
            className="mt-auto pt-6 text-sm font-bold text-blue-400 hover:text-blue-300 flex items-center gap-2 transition-colors w-fit focus-visible:ring-2 focus-visible:ring-blue-500/50 rounded"
          >
            View full audit trail <ChevronRight className="w-4 h-4" />
          </button>
        </Card>
      </div>
    </div>
  );
};

export default DashboardScreen;
